import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { staticInsights } from '@/data/staticInsights';

export default function InsightsPage() {
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');
  const insights = staticInsights;

  const categories = ['All', ...new Set(insights.map(i => i.category))];

  const filtered = insights.filter(i => {
    const matchCat = filter === 'All' || i.category === filter;
    const matchSearch = !search || i.title.toLowerCase().includes(search.toLowerCase()) || i.excerpt.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  return (
    <div>
      {/* Hero */}
      <section data-testid="insights-hero" className="py-24 md:py-32 bg-secondary">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">Insights & Resources</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-navy leading-[1.1] mb-6">
            Thought Leadership
          </h1>
          <p className="text-base md:text-lg text-navy/60 max-w-2xl leading-relaxed">
            Expert perspectives on AI strategy, industry trends, and emerging technologies shaping the future of enterprise.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="py-8 bg-white border-b border-border sticky top-16 md:top-20 z-40 backdrop-blur-xl bg-white/90">
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex flex-wrap gap-2" data-testid="insights-filters">
            {categories.map(cat => (
              <button
                key={cat}
                data-testid={`filter-${cat.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={() => setFilter(cat)}
                className={`px-4 py-1.5 rounded-full text-xs font-medium transition-colors ${
                  filter === cat
                    ? 'bg-navy text-white'
                    : 'bg-secondary text-navy/60 hover:bg-navy/5'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="relative w-full md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-navy/30" />
            <Input
              data-testid="insights-search"
              placeholder="Search insights..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 bg-secondary border-0 rounded-full text-sm"
            />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section data-testid="insights-grid" className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-navy/40 text-lg">No insights found matching your criteria.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {filtered.map((post, i) => (
                <Link
                  key={post.slug}
                  to={`/insights/${post.slug}`}
                  data-testid={`insight-card-${i}`}
                  className="group"
                >
                  <div className="aspect-[16/10] rounded-sm overflow-hidden mb-4">
                    <img
                      src={post.image_url}
                      alt={post.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <Badge variant="secondary" className="bg-teal/10 text-teal border-0 text-xs rounded-full">
                      {post.category}
                    </Badge>
                    {post.industry && (
                      <span className="text-xs text-navy/40">{post.industry}</span>
                    )}
                  </div>
                  <h3 className="font-heading text-lg font-medium text-navy group-hover:text-teal transition-colors mb-2">
                    {post.title}
                  </h3>
                  <p className="text-sm text-navy/50 leading-relaxed line-clamp-2 mb-3">{post.excerpt}</p>
                  <div className="flex items-center gap-2 text-xs text-navy/40">
                    <span>{post.author}</span>
                    <span>|</span>
                    <span>{new Date(post.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}</span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
